import { useState, useEffect } from 'react'
import { Zap, RefreshCw, AlertCircle, TrendingUp, MessageSquare, DollarSign, Wallet, CheckCircle, Pencil } from 'lucide-react'
import { usageService, UsageOverview, UsagePeriod, OpenAiBillingData } from '../../services/usageService'
import { formatUsd, formatTokens, formatInteger, formatPct, formatDayMonth, formatDate } from '../../lib/formatters'

const PERIOD_LABELS: Record<UsagePeriod, string> = {
  today: 'Hoje',
  week: 'Esta Semana',
  month: 'Este Mês',
  all: 'Todo o Período',
}

function CalcBadge() {
  return (
    <span className="text-[9px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded font-normal ml-1 align-middle">calc</span>
  )
}

function LiveBadge() {
  return (
    <span className="text-[9px] bg-green-100 text-green-700 px-1 py-0.5 rounded font-normal ml-1 align-middle">live</span>
  )
}

export function OpenAiStatsCard() {
  const [data, setData] = useState<UsageOverview | null>(null)
  const [billing, setBilling] = useState<OpenAiBillingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [billingLoading, setBillingLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState<UsagePeriod>('month')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [editingBudget, setEditingBudget] = useState(false)
  const [budgetInput, setBudgetInput] = useState('')
  const [savingBudget, setSavingBudget] = useState(false)

  const load = async (p: UsagePeriod) => {
    try {
      setLoading(true)
      setError(null)
      const result = await usageService.getUsageOverview(p)
      setData(result)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Failed to load usage overview:', err)
      setError('Não foi possível carregar métricas de uso')
    } finally {
      setLoading(false)
    }
  }

  const loadBilling = async (refresh = false) => {
    try {
      setBillingLoading(true)
      const result = await usageService.getBillingData(refresh)
      setBilling(result)
    } catch (err) {
      console.error('Failed to load billing data:', err)
      setBilling(null)
    } finally {
      setBillingLoading(false)
    }
  }

  const saveBudget = async () => {
    const value = parseFloat(budgetInput.replace(',', '.'))
    if (isNaN(value) || value < 0) return
    try {
      setSavingBudget(true)
      await usageService.setMonthlyBudget(value)
      setEditingBudget(false)
      await loadBilling()
    } catch (err) {
      console.error('Failed to save budget:', err)
    } finally {
      setSavingBudget(false)
    }
  }

  useEffect(() => {
    load(period)
  }, [period])

  useEffect(() => {
    loadBilling()
  }, [])

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  const maxBarTokens = data?.byModel?.length
    ? Math.max(...data.byModel.map(m => m.tokens))
    : 1

  const realCostAvailable = billing?.configured && billing.currentMonth.totalCostUsd != null

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 shadow rounded-lg overflow-hidden">
      <div className="px-4 sm:px-6 py-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-2 bg-purple-100 rounded-lg">
              <Zap className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Uso de LLM (OpenAI)</h3>
              <p className="text-sm text-gray-600 flex items-center gap-1 flex-wrap">
                {realCostAvailable
                  ? <><span>Tokens do sistema</span><LiveBadge /><span className="mx-1">·</span><span>Custo real via OpenAI</span><LiveBadge /></>
                  : <><span>Tokens do sistema</span><LiveBadge /><span className="mx-1">·</span><span>Custo calculado</span><CalcBadge /></>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as UsagePeriod)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {lastUpdated && (
              <span className="text-[10px] text-gray-400 hidden sm:block">
                {lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => { load(period); loadBilling(true) }}
              className="p-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-100 rounded-md transition-colors"
              title="Atualizar"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Budget Panel — orçamento mensal vs custo real da OpenAI */}
        {billingLoading ? (
          <div className="mt-5 animate-pulse h-28 bg-white rounded-lg shadow-sm" />
        ) : billing?.configured && billing.error ? (
          <div className="mt-5 bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-orange-800">{billing.error}</p>
          </div>
        ) : realCostAvailable ? (() => {
          const used = billing!.currentMonth.totalCostUsd ?? 0
          const budget = billing!.budget
          const pct = budget ? budget.pctUsed : null
          const barColor = pct == null ? 'bg-green-500' : pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-orange-400' : 'bg-green-500'
          return (
            <div className="mt-5 bg-white rounded-lg shadow-sm p-4 border border-green-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-semibold text-gray-900">Orçamento — OpenAI</span>
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Live
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {formatDate(billing!.period.startDate)} → {formatDate(billing!.period.endDate)}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-500">Gasto este mês</p>
                  <p className="text-lg font-bold text-orange-600">{formatUsd(used)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    Orçamento mensal
                    {!editingBudget && (
                      <button
                        onClick={() => {
                          setBudgetInput(budget ? String(budget.monthlyUsd) : '')
                          setEditingBudget(true)
                        }}
                        className="text-gray-400 hover:text-purple-600"
                        title="Editar orçamento"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </p>
                  {editingBudget ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <input
                        type="number"
                        min="0"
                        step="10"
                        value={budgetInput}
                        onChange={(e) => setBudgetInput(e.target.value)}
                        className="w-20 text-sm border border-gray-300 rounded px-1.5 py-0.5"
                        placeholder="USD"
                        autoFocus
                      />
                      <button
                        onClick={saveBudget}
                        disabled={savingBudget}
                        className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded hover:bg-purple-700 disabled:opacity-50"
                      >
                        {savingBudget ? '...' : 'OK'}
                      </button>
                      <button
                        onClick={() => setEditingBudget(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <p className="text-lg font-bold text-gray-700">
                      {budget ? formatUsd(budget.monthlyUsd) : '—'}
                    </p>
                  )}
                </div>
                {budget && (
                  <div>
                    <p className="text-xs text-gray-500">Disponível</p>
                    <p className={`text-lg font-bold ${budget.remainingUsd < budget.monthlyUsd * 0.1 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatUsd(budget.remainingUsd)}
                    </p>
                  </div>
                )}
                {billing!.reconciliation && (
                  <div>
                    <p className="text-xs text-gray-500">Cobertura do tracking</p>
                    <p className={`text-lg font-bold ${billing!.reconciliation.coveragePct >= 90 ? 'text-green-600' : 'text-orange-600'}`}>
                      {formatPct(billing!.reconciliation.coveragePct)}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {formatTokens(billing!.reconciliation.trackedTokens)} de {formatTokens(billing!.reconciliation.openaiTokens)} tokens
                    </p>
                  </div>
                )}
              </div>
              {pct != null && (
                <div>
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                    <span>{formatPct(pct)} do orçamento usado</span>
                    {budget && <span>{formatUsd(budget.remainingUsd)} restantes</span>}
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )}
              {!budget && (
                <p className="text-[10px] text-gray-400">
                  Defina um orçamento mensal para acompanhar o valor disponível e receber a barra de progresso.
                </p>
              )}
            </div>
          )
        })() : billing && !billing.configured ? (
          <div className="mt-5 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
            <p className="text-xs text-yellow-800">Billing real indisponível — configure <code className="font-mono">OPENAI_ADMIN_KEY</code> no auth-service.</p>
          </div>
        ) : null}

        {/* Projection + Efficiency Cards — only when data available */}
        {!loading && data && (data.totals.tokens > 0 || (realCostAvailable && (billing!.currentMonth.totalCostUsd ?? 0) > 0)) && (() => {
          const realCost = realCostAvailable && period === 'month' ? (billing!.currentMonth.totalCostUsd ?? null) : null
          const baseCost = realCost ?? data.totals.costUsd
          const today = new Date()
          const daysElapsed = today.getDate()
          const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
          const projectedMonthCost = daysElapsed > 0 && period === 'month'
            ? (baseCost / daysElapsed) * daysInMonth
            : null
          const avgTokensPerReq = data.totals.requests > 0
            ? Math.round(data.totals.tokens / data.totals.requests)
            : null
          const activeUsers = data.totals.activeUsers || 0
          const costPerUser = activeUsers > 0 && baseCost > 0
            ? baseCost / activeUsers
            : null
          const peakDay = data.dailyTrend.length > 0
            ? data.dailyTrend.reduce((a, b) => a.tokens > b.tokens ? a : b)
            : null
          const hasRealCost = realCost != null && period === 'month'
          return (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {projectedMonthCost != null && (
                <div className="bg-white rounded-lg p-3 shadow-sm border-l-2 border-orange-400">
                  <p className="text-[10px] text-gray-500 flex items-center">Projeção Mês{hasRealCost ? <LiveBadge /> : <CalcBadge />}</p>
                  <p className="text-base font-bold text-orange-600">{formatUsd(projectedMonthCost)}</p>
                  <p className="text-[10px] text-gray-400">ao ritmo atual ({daysElapsed}d/{daysInMonth}d)</p>
                </div>
              )}
              {costPerUser != null && (
                <div className="bg-white rounded-lg p-3 shadow-sm border-l-2 border-purple-400">
                  <p className="text-[10px] text-gray-500 flex items-center">Custo/Usuário{hasRealCost ? <LiveBadge /> : <CalcBadge />}</p>
                  <p className="text-base font-bold text-purple-600">{formatUsd(costPerUser)}</p>
                  <p className="text-[10px] text-gray-400">entre {formatInteger(activeUsers)} usuários ativos</p>
                </div>
              )}
              {avgTokensPerReq != null && (
                <div className="bg-white rounded-lg p-3 shadow-sm border-l-2 border-indigo-400">
                  <p className="text-[10px] text-gray-500 flex items-center">Média Tokens/Req<LiveBadge /></p>
                  <p className="text-base font-bold text-indigo-600">{formatTokens(avgTokensPerReq)}</p>
                  <p className="text-[10px] text-gray-400">por interação de chat</p>
                </div>
              )}
              {peakDay != null && (
                <div className="bg-white rounded-lg p-3 shadow-sm border-l-2 border-pink-400">
                  <p className="text-[10px] text-gray-500 flex items-center">Pico de Uso<LiveBadge /></p>
                  <p className="text-base font-bold text-pink-600">{formatTokens(peakDay.tokens)}</p>
                  <p className="text-[10px] text-gray-400">{formatDayMonth(peakDay.day)}</p>
                </div>
              )}
            </div>
          )
        })()}

        {/* Stats Cards */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total de Tokens</p>
                <p className="mt-1 text-xl sm:text-2xl font-bold text-purple-600 truncate">
                  {formatTokens(data?.totals.tokens || 0)}
                </p>
                {(data?.totals.promptTokens || 0) > 0 && (
                  <div className="mt-1 flex gap-2 text-[10px]">
                    <span className="text-blue-600">↑ {formatTokens(data!.totals.promptTokens)} in</span>
                    <span className="text-emerald-600">↓ {formatTokens(data!.totals.completionTokens)} out</span>
                  </div>
                )}
              </div>
              <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-purple-200 flex-shrink-0" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                {realCostAvailable && period === 'month' ? (
                  <>
                    <p className="text-xs sm:text-sm font-medium text-gray-600 flex items-center gap-1">
                      Custo Real
                      <span className="text-[9px] bg-green-100 text-green-700 px-1 rounded">OpenAI</span>
                    </p>
                    <p className="mt-1 text-xl sm:text-2xl font-bold text-orange-600 truncate">
                      {formatUsd(billing!.currentMonth.totalCostUsd)}
                    </p>
                    <p className="text-[10px] text-gray-400">tracking interno: {formatUsd(data?.totals.costUsd || 0)}</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs sm:text-sm font-medium text-gray-600 flex items-center gap-1">
                      Custo
                      {data?.totals.costIsEstimated ? <CalcBadge /> : <LiveBadge />}
                    </p>
                    <p className="mt-1 text-xl sm:text-2xl font-bold text-orange-600 truncate">
                      {formatUsd(data?.totals.costUsd || 0)}
                    </p>
                  </>
                )}
              </div>
              <DollarSign className="h-8 w-8 sm:h-10 sm:w-10 text-orange-200 flex-shrink-0" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Requisições</p>
                <p className="mt-1 text-xl sm:text-2xl font-bold text-indigo-600 truncate">
                  {formatInteger(data?.totals.requests || 0)}
                </p>
              </div>
              <MessageSquare className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-200 flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* By Model */}
        {data?.byModel && data.byModel.length > 0 && (
          <div className="mt-5">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">Por Modelo</h4>
            <div className="space-y-2">
              {data.byModel.map((m) => {
                const hasBreakdown = (m.promptTokens || 0) > 0
                const promptPct = hasBreakdown && m.tokens > 0 ? (m.promptTokens / m.tokens) * 100 : 40
                const completionPct = 100 - promptPct
                return (
                  <div key={m.model} className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-mono font-medium text-gray-900 truncate">{m.model}</span>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 flex-shrink-0">
                        <span>{formatTokens(m.tokens)} total</span>
                        <span>{formatInteger(m.requests)} req</span>
                        <span className="font-semibold text-orange-600 flex items-center">{formatUsd(m.costUsd)}</span>
                      </div>
                    </div>
                    {hasBreakdown && (
                      <div className="mt-1.5 flex gap-3 text-[10px]">
                        <span className="text-blue-600">↑ {formatTokens(m.promptTokens)} in ({promptPct.toFixed(0)}%)</span>
                        <span className="text-emerald-600">↓ {formatTokens(m.completionTokens)} out ({completionPct.toFixed(0)}%)</span>
                      </div>
                    )}
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-blue-400 rounded-l-full transition-all"
                        style={{ width: `${Math.max(1, (m.tokens / maxBarTokens) * promptPct)}%` }}
                        title="Tokens de input (prompt)"
                      />
                      <div
                        className="h-full bg-emerald-400 rounded-r-full transition-all"
                        style={{ width: `${Math.max(1, (m.tokens / maxBarTokens) * completionPct)}%` }}
                        title="Tokens de output (completion)"
                      />
                    </div>
                    <div className="mt-1 flex gap-3 text-[9px] text-gray-400">
                      <span className="flex items-center gap-0.5"><span className="inline-block w-2 h-1.5 bg-blue-400 rounded-sm"></span>input</span>
                      <span className="flex items-center gap-0.5"><span className="inline-block w-2 h-1.5 bg-emerald-400 rounded-sm"></span>output</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* By Service */}
        {data?.byService && data.byService.length > 0 && (() => {
          const SERVICE_LABELS: Record<string, string> = {
            chat: 'Chat',
            chat_stream: 'Chat (stream)',
            chat_media: 'Chat (mídia)',
            title_generation: 'Geração de título',
            rag_embedding: 'RAG — Embeddings',
            rag_summarization: 'RAG — Sumarização',
            whatsapp_extraction: 'WhatsApp — Extração',
          }
          const totalCost = data.byService.reduce((s, x) => s + (x.costUsd || 0), 0)
          const maxTokens = Math.max(...data.byService.map(s => s.tokens))
          return (
            <div className="mt-5">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Consumo por Serviço</h4>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Serviço</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tokens</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Req</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Custo</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Proporção</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.byService.map((s) => {
                      const pct = maxTokens > 0 ? (s.tokens / maxTokens) * 100 : 0
                      return (
                        <tr key={s.service} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900 font-medium">
                            {SERVICE_LABELS[s.service] || s.service.replace(/_/g, ' ')}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-purple-600">{formatTokens(s.tokens)}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{formatInteger(s.requests)}</td>
                          <td className="px-3 py-2 text-right font-mono text-orange-600">{formatUsd(s.costUsd || 0)}</td>
                          <td className="px-3 py-2">
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className="bg-purple-400 rounded-full h-2" style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  {totalCost > 0 && (
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-3 py-2 text-gray-900" colSpan={3}>Total rastreado</td>
                        <td className="px-3 py-2 text-right font-mono text-orange-700">{formatUsd(totalCost)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )
        })()}

        {/* Top Users */}
        {data?.topUsers && data.topUsers.length > 0 && (() => {
          const maxUserTokens = Math.max(...data.topUsers.map(u => u.tokens))
          return (
            <div className="mt-5">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Top Consumidores</h4>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tokens</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Req</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Custo<LiveBadge />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.topUsers.map((u, i) => {
                        const pct = maxUserTokens > 0 ? (u.tokens / maxUserTokens) * 100 : 0
                        return (
                          <tr key={u.userId} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-400 font-mono">{i + 1}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900 truncate max-w-[160px]">{u.name}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[160px]">{u.email}</div>
                              <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden w-full max-w-[160px]">
                                <div className="h-full bg-purple-300 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-purple-600">{formatTokens(u.tokens)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{formatInteger(u.requests)}</td>
                            <td className="px-3 py-2 text-right text-orange-600 font-mono text-xs">{formatUsd(u.costUsd)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )
        })()}

        {/* Daily Trend - Simple bar chart */}
        {data?.dailyTrend && data.dailyTrend.length > 0 && (
          <div className="mt-5">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Tendência Diária (últimos 30 dias)</h4>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-end gap-[2px] sm:gap-1 h-24 sm:h-32">
                {data.dailyTrend.map((d) => {
                  const maxTokens = Math.max(...data.dailyTrend.map(x => x.tokens))
                  const height = maxTokens > 0 ? Math.max(2, (d.tokens / maxTokens) * 100) : 0
                  return (
                    <div
                      key={d.day}
                      className="flex-1 bg-purple-400 hover:bg-purple-600 rounded-sm transition-colors"
                      style={{ height: `${height}%` }}
                      title={`${formatDate(d.day)}: ${formatTokens(d.tokens)} tokens, ${formatInteger(d.requests)} req`}
                    />
                  )
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] sm:text-xs text-gray-400">
                <span>{formatDayMonth(data.dailyTrend[0].day)}</span>
                <span>{formatDayMonth(data.dailyTrend[data.dailyTrend.length - 1].day)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Daily Cost Chart — OpenAI billing real */}
        {billing?.configured && billing.currentMonth.dailyCosts && billing.currentMonth.dailyCosts.length > 0 && (
          <div className="mt-5">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Custo Diário — Dados Reais OpenAI (mês atual)</h4>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-end gap-[2px] sm:gap-1 h-24 sm:h-32">
                {billing.currentMonth.dailyCosts.map((d) => {
                  const maxCost = Math.max(...billing.currentMonth.dailyCosts.map(x => x.costUsd))
                  const height = maxCost > 0 ? Math.max(2, (d.costUsd / maxCost) * 100) : 2
                  const tooltipLines = d.byModel.map(m => `${m.name}: ${formatUsd(m.costUsd)}`).join(' | ')
                  return (
                    <div
                      key={d.date}
                      className="flex-1 bg-orange-400 hover:bg-orange-600 rounded-sm transition-colors"
                      style={{ height: `${height}%` }}
                      title={`${formatDate(d.date)}: ${formatUsd(d.costUsd)}${tooltipLines ? ' — ' + tooltipLines : ''}`}
                    />
                  )
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] sm:text-xs text-gray-400">
                <span>{formatDayMonth(billing.currentMonth.dailyCosts[0].date)}</span>
                <span className="text-orange-600 font-semibold">
                  Total: {formatUsd(billing.currentMonth.totalCostUsd ?? 0)}
                </span>
                <span>{formatDayMonth(billing.currentMonth.dailyCosts[billing.currentMonth.dailyCosts.length - 1].date)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {data?.totals.tokens === 0 && (
          <div className="mt-5 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              Nenhum uso registrado para o período selecionado. Os tokens serão rastreados automaticamente a cada interação de chat.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
